import TextArea from '@atlaskit/textarea';
import { SeamlessTextArea } from '@databases/form/styled';
import { Metrics } from '@databases/frontend-metrics';
import { useTypingPerformance } from '@databases/frontend-metrics/useTypingPerformance';
import { useTextDelta } from '@databases/react/useTextDelta';
import { DatabaseFieldType } from '@databases/types/database';
import composeRefs from '@seznam/compose-react-refs';
import React, { type FunctionComponent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { type EditComponentProps } from './types';

export const Container = styled.div`
  // The text area wrapper must not be a flex box, otherwise the text area is blockified and its height is ignored

  flex-grow: 1;

  &&& textarea {
    // The height calculation algorithm of @atlaskit/textarea sets the height to auto and then sets the height
    // to its scroll height. With height: auto, a scrollbar appears (except on Atlassian's Mac devices), causing
    // a different scroll height than later when the scroll bar disappears again.
    // Hiding the scrollbar fixes the height calculation algorithm.
    overflow: hidden;

    min-height: 100%;
  }
`;

const TextValueEdit: FunctionComponent<EditComponentProps<DatabaseFieldType.TEXT>> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isSeamless,
  initialKey,
  'aria-describedby': ariaDescribedBy,
}) => {
  const Component = isSeamless ? SeamlessTextArea : TextArea;

  const [inputValue, setInputValue] = useState(initialKey ?? value.plain);

  useEffect(() => {
    if (initialKey !== undefined) {
      const oldLength = value.plain.length;
      const delta =
        oldLength > 0
          ? [{ delete: oldLength }, { insert: initialKey }]
          : [{ insert: initialKey }];
      onChange({ plain: initialKey }, { type: DatabaseFieldType.TEXT, delta });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );
      onFocus?.(e);
    },
    [onFocus]
  );

  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);
  useTextDelta(
    textareaRef,
    useCallback(
      async (delta, newValue) => {
        setInputValue(newValue);
        onChange(
          { plain: newValue },
          {
            type: DatabaseFieldType.TEXT,
            delta,
          }
        );
      },
      [onChange]
    )
  );

  const typingPerformanceRef = useTypingPerformance<HTMLTextAreaElement>(
    Metrics.MEAN_TEXT_VALUE_TYPING
  );

  return (
    <Container>
      <Component
        value={inputValue}
        onFocus={handleFocus}
        onBlur={onBlur}
        autoFocus={autoFocus}
        rows={1}
        ref={composeRefs(setTextareaRef, typingPerformanceRef)}
        testId={'text-value-edit'}
        aria-describedby={ariaDescribedBy}
      />
    </Container>
  );
};

export default TextValueEdit;
